import React, { useEffect, useMemo, useState } from 'react';
import { friendlyPastTime } from '../../lib/friendlyTime';
import ManualReminderDialog from './ManualReminderDialog';

const API = process.env.REACT_APP_API_URL;

export default function RequestDetail({ requestId, onBack }) {
  const [req, setReq] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sending, setSending] = useState(false);

  function load() {
    setLoading(true);
    Promise.all([
      fetch(`${API}/api/universe-requests/${requestId}`, { credentials: 'include' }).then(r => r.json()),
      fetch(`${API}/api/universe-requests/${requestId}/submissions`, { credentials: 'include' }).then(r => r.json()).catch(() => ({ submissions: [] })),
    ])
      .then(([reqData, subData]) => {
        if (reqData.request) setReq(reqData.request);
        else setError(reqData.error || 'Not found');
        setSubmissions(subData.submissions || []);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }
  useEffect(load, [requestId]);

  async function cancel() {
    if (!window.confirm('Cancel this request?')) return;
    const res = await fetch(`${API}/api/universe-requests/${requestId}/cancel`, { method: 'POST', credentials: 'include' });
    if (res.ok) load();
  }
  async function send() {
    if (!window.confirm('Send the invitation email to the FDR now?')) return;
    setSending(true);
    try {
      const res = await fetch(`${API}/api/universe-requests/${requestId}/send`, { method: 'POST', credentials: 'include' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Send failed');
      load();
    } catch (e) { alert(e.message); }
    finally { setSending(false); }
  }
  function downloadFile(submissionId, fileName) {
    const url = `${API}/api/universe-requests/${requestId}/submissions/${submissionId}/download`;
    const a = document.createElement('a');
    a.href = url; a.download = fileName || 'submission'; document.body.appendChild(a); a.click(); a.remove();
  }

  if (loading) return <div className="text-sm text-slate-500">Loading…</div>;
  if (error) return <div className="border border-red-200 bg-red-50 px-3 py-2 text-red-800 text-sm">{error}</div>;
  if (!req) return null;

  return (
    <div>
      <button onClick={onBack} className="text-xs font-mono uppercase tracking-widest text-slate-500 hover:text-slate-900 mb-4 transition-colors">
        ← All requests
      </button>

      <div className="bg-white border border-slate-200 p-6 mb-4">
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="text-xs font-mono uppercase tracking-widest text-red-700 mb-1">Request</div>
            <h2 className="text-lg font-semibold text-slate-900">{req.title}</h2>
          </div>
          <div className="flex gap-2">
            {req.status === 'draft' && (
              <button onClick={send} disabled={sending}
                className="bg-red-700 hover:bg-red-800 disabled:bg-slate-300 text-white px-4 py-1.5 text-sm font-semibold transition-colors">
                {sending ? 'Sending…' : 'Send invitation →'}
              </button>
            )}
            {['draft', 'sent', 'in_progress'].includes(req.status) && (
              <button onClick={cancel} className="border border-slate-300 text-slate-700 hover:border-red-700 hover:text-red-700 px-3 py-1 text-xs font-mono uppercase transition-colors">
                Cancel
              </button>
            )}
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          {req.recipient_type === 'internal_department' ? (
            <Field label="Department" value={req.department_name || 'Internal department'}/>
          ) : (
            <>
              <Field label="FDR" value={req.fdr_name}/>
              <Field label="Email" mono value={req.fdr_email}/>
            </>
          )}
          <Field label="Purpose" value={req.purpose}/>
          <Field label="Status" value={req.status}/>
          {req.reporting_period_start && (
            <Field label="Period" mono value={`${req.reporting_period_start} → ${req.reporting_period_end}`}/>
          )}
          <Field label="Created" value={<span title={new Date(req.created_at).toLocaleString()}>{friendlyPastTime(req.created_at)}</span>}/>
          {req.sent_at && <Field label="Sent" value={<span title={new Date(req.sent_at).toLocaleString()}>{friendlyPastTime(req.sent_at)}</span>}/>}
          {req.completed_at && <Field label="Completed" value={<span title={new Date(req.completed_at).toLocaleString()}>{friendlyPastTime(req.completed_at)}</span>}/>}
        </div>
        {req.message_to_fdr && (
          <div className="mt-4 border-l-4 border-slate-200 pl-3 text-sm text-slate-700">
            <div className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-1">Message to FDR</div>
            {req.message_to_fdr}
          </div>
        )}
      </div>

      {req.acknowledged_at && ['sent', 'in_progress'].includes(req.status) && (
        <div className="border border-amber-300 bg-amber-50 px-4 py-3 mb-4 text-sm text-amber-900">
          <div className="text-xs font-mono uppercase tracking-widest text-amber-700 mb-1">Reported as submitted</div>
          <span className="font-semibold">{req.acknowledged_by || 'The recipient'}</span> reported on {new Date(req.acknowledged_at).toLocaleDateString()}
          {req.acknowledged_channel === 'portal' ? ' from the FDR portal' : ' from the app'} that the universe{(req.items || []).length === 1 ? ' was' : 's were'} already submitted
          {req.acknowledged_note ? <>: <span className="italic">{req.acknowledged_note}</span></> : '.'}
          {' '}Reminders are paused. Confirm by marking each universe received below, or resume the reminders if nothing arrived.
        </div>
      )}

      {['sent', 'in_progress'].includes(req.status) && (
        <RemindersBlock requestId={requestId} title={req.title} onChanged={load}/>
      )}

      <ItemsAndSubmissions
        req={req}
        submissions={submissions}
        onDownload={downloadFile}
        onChanged={load}
      />
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Reminders block: snooze state + next-reminder forecast + history.
// Default behavior is ON — Compliance only interacts when they want to pause
// nudges (e.g. they're already on a phone call with the FDR).
// ──────────────────────────────────────────────────────────────────────────────

function RemindersBlock({ requestId, title, onChanged }) {
  const [state, setState] = useState({ reminders: [], forecast: null, snoozed_until: null });
  const [showHistory, setShowHistory] = useState(false);
  const [busy, setBusy] = useState(false);
  const [remindOpen, setRemindOpen] = useState(false);

  function load() {
    fetch(`${API}/api/universe-requests/${requestId}/reminders`, { credentials: 'include' })
      .then(r => r.json()).then(setState).catch(() => {});
  }
  useEffect(load, [requestId]);

  async function snooze(option) {
    let until = 'forever', reason = null;
    if (option === '24h') { until = new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(); reason = '24-hour pause'; }
    else if (option === '7d') { until = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(); reason = '7-day pause'; }
    else if (option === 'forever') { until = 'forever'; reason = window.prompt('Reason (optional)') || 'Indefinite pause'; }
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/universe-requests/${requestId}/snooze-reminders`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ until, reason }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || 'Snooze failed');
      load(); onChanged && onChanged();
    } catch (e) { alert(e.message); }
    finally { setBusy(false); }
  }
  async function resume() {
    setBusy(true);
    try {
      const res = await fetch(`${API}/api/universe-requests/${requestId}/resume-reminders`, {
        method: 'POST', credentials: 'include',
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || 'Resume failed');
      load(); onChanged && onChanged();
    } catch (e) { alert(e.message); }
    finally { setBusy(false); }
  }

  const isSnoozed = state.snoozed_until && new Date(state.snoozed_until) > new Date();
  const forecast = state.forecast;

  return (
    <div className="bg-white border border-slate-200 p-6 mb-4">
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="text-xs font-mono uppercase tracking-widest text-red-700 mb-1">Reminders</div>
          {isSnoozed ? (
            <div className="text-sm text-slate-900">
              <span className="font-semibold text-amber-700">{new Date(state.snoozed_until).getFullYear() >= 2999 ? 'Snoozed indefinitely' : 'Snoozed'}</span>
              {state.snoozed_until && new Date(state.snoozed_until).getFullYear() < 2999 && (
                <> until {new Date(state.snoozed_until).toLocaleString()}</>
              )}
              {state.snooze_reason ? <span className="block text-xs text-slate-600 mt-0.5">{state.snooze_reason}</span> : null}
            </div>
          ) : forecast?.type === 'snoozed' ? (
            <div className="text-sm text-slate-900"><span className="font-semibold text-amber-700">Snoozed indefinitely</span></div>
          ) : (
            <div className="text-sm text-slate-900">
              <span className="font-semibold text-emerald-700">Active.</span>{' '}
              {forecast?.at ? (
                <>Next nudge: <span className="font-mono">{labelForForecastType(forecast.type)}</span> ({new Date(forecast.at).toLocaleString()}).</>
              ) : (
                <>No upcoming reminders (the request has no due date).</>
              )}
            </div>
          )}
          <div className="text-xs text-slate-500 mt-1">
            72h before due · 24h before · day-of · daily once overdue · escalation to you at 72h overdue.
            {' '}A reminder sent by hand goes out now and does not change this schedule.
          </div>
        </div>
        <div className="flex flex-col gap-1 flex-shrink-0">
          <button disabled={busy} onClick={() => setRemindOpen(true)}
            className="bg-red-700 hover:bg-red-800 disabled:bg-slate-300 text-white px-3 py-1 text-xs font-mono uppercase tracking-wide transition-colors">
            Send reminder now
          </button>
          {isSnoozed ? (
            <button disabled={busy} onClick={resume}
              className="bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300 text-white px-3 py-1 text-xs font-mono uppercase tracking-wide transition-colors">
              Resume reminders
            </button>
          ) : (
            <>
              <button disabled={busy} onClick={() => snooze('24h')}
                className="border border-slate-300 hover:border-amber-500 hover:text-amber-700 text-slate-700 px-3 py-1 text-xs font-mono uppercase tracking-wide transition-colors">
                Pause 24h
              </button>
              <button disabled={busy} onClick={() => snooze('7d')}
                className="border border-slate-300 hover:border-amber-500 hover:text-amber-700 text-slate-700 px-3 py-1 text-xs font-mono uppercase tracking-wide transition-colors">
                Pause 7 days
              </button>
              <button disabled={busy} onClick={() => snooze('forever')}
                className="border border-slate-300 hover:border-amber-500 hover:text-amber-700 text-slate-700 px-3 py-1 text-xs font-mono uppercase tracking-wide transition-colors">
                Pause indefinitely
              </button>
            </>
          )}
        </div>
      </div>

      {state.reminders.length > 0 && (
        <div>
          <button onClick={() => setShowHistory(!showHistory)}
            className="text-[10px] font-mono uppercase tracking-widest text-slate-500 hover:text-slate-900 transition-colors">
            {showHistory ? 'Hide' : 'Show'} reminder history ({state.reminders.length})
          </button>
          {showHistory && (
            <table className="w-full text-xs mt-2 border-t border-slate-100">
              <thead>
                <tr className="text-[10px] font-mono uppercase tracking-wide text-slate-500">
                  <th className="text-left py-1">When</th>
                  <th className="text-left py-1">Type</th>
                  <th className="text-left py-1">Sent</th>
                </tr>
              </thead>
              <tbody>
                {state.reminders.map(r => (
                  <tr key={r.id} className="border-t border-slate-50">
                    <td className="py-1 font-mono text-slate-700" title={new Date(r.fired_at).toLocaleString()}>{friendlyPastTime(r.fired_at)}</td>
                    <td className="py-1 text-slate-700">{labelForReminderType(r.reminder_type)}{r.reminder_type === 'manual' && r.sent_by_name ? ` by ${r.sent_by_name}` : ''}</td>
                    <td className="py-1 font-mono text-slate-700">{r.sent_count}/{r.recipient_count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
      {remindOpen && (
        <ManualReminderDialog requestId={requestId} title={title}
          onClose={() => setRemindOpen(false)} onSent={() => { load(); onChanged && onChanged(); }} />
      )}
    </div>
  );
}

function labelForReminderType(t) {
  return ({
    '72h_before': '3 days before due',
    '24h_before': '1 day before due',
    'due_today': 'Due today',
    'overdue_daily': 'Overdue (daily)',
    'compliance_escalation': 'Compliance escalation',
    'manual': 'Sent by hand',
  })[t] || t;
}
function labelForForecastType(t) {
  return ({
    '72h_before': '3 days before due',
    '24h_before': '1 day before due',
    'due_today': 'on the due date',
    'overdue_daily': 'tomorrow (daily overdue)',
    'compliance_escalation_or_daily': 'overdue daily + compliance escalation',
    'snoozed': 'snoozed',
  })[t] || t;
}

function Field({ label, value, mono }) {
  return (
    <div>
      <div className="text-xs font-mono uppercase tracking-widest text-slate-500">{label}</div>
      <div className={mono ? 'font-mono text-xs text-slate-800 mt-0.5' : 'text-slate-800 mt-0.5'}>{value}</div>
    </div>
  );
}

function ItemsAndSubmissions({ req, submissions, onDownload, onChanged }) {
  const canReceive = ['sent', 'in_progress'].includes(req.status);
  // Group submissions per item, sorted by attempt_number desc.
  const subsByItem = useMemo(() => {
    const map = new Map();
    for (const s of submissions) {
      if (!map.has(s.item_id)) map.set(s.item_id, []);
      map.get(s.item_id).push(s);
    }
    for (const list of map.values()) list.sort((a, b) => b.attempt_number - a.attempt_number);
    return map;
  }, [submissions]);

  return (
    <div className="bg-white border border-slate-200 p-6">
      <h3 className="text-sm font-semibold text-slate-900 mb-4">Requested universes ({req.items?.length || 0})</h3>
      <div className="space-y-4">
        {(req.items || []).map(it => {
          const subs = subsByItem.get(it.id) || [];
          const finalSub = subs.find(s => s.finalized) || subs[0];
          return (
            <ItemSubmissionBlock
              key={it.id}
              item={it}
              submissions={subs}
              finalSub={finalSub}
              onDownload={onDownload}
              requestId={req.id}
              recipientType={req.recipient_type}
              canReceive={canReceive}
              onChanged={onChanged}
            />
          );
        })}
      </div>
      {req.status === 'draft' && (
        <div className="mt-4 border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          This request is a <span className="font-semibold">draft</span>. The FDR has not been notified yet — click <span className="font-semibold">Send invitation</span> to email the magic-link.
        </div>
      )}
    </div>
  );
}

function ItemSubmissionBlock({ item, submissions, finalSub, onDownload, requestId, recipientType, canReceive, onChanged }) {
  const [open, setOpen] = useState(!!finalSub);
  const [marking, setMarking] = useState(false);
  const [markNote, setMarkNote] = useState('');
  const [markBusy, setMarkBusy] = useState(false);
  const [markError, setMarkError] = useState(null);

  async function markReceived() {
    setMarkBusy(true); setMarkError(null);
    try {
      const res = await fetch(`${API}/api/universe-requests/${requestId}/items/${item.id}/mark-received`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note: markNote.trim() || null }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Could not mark the universe received.');
      setMarking(false);
      if (onChanged) onChanged();
    } catch (e) { setMarkError(e.message); }
    finally { setMarkBusy(false); }
  }
  const consumerKind = item.consumer_app === 'coa' ? 'RESULT' : item.consumer_app === 'scrubber' ? 'AUDIT' : 'REPORTING';
  const itemBorder =
    item.status === 'accepted' ? 'border-l-4 border-emerald-500' :
    item.status === 'received' ? 'border-l-4 border-amber-500' :
                                  'border-l-4 border-transparent';

  return (
    <div className={`border border-slate-200 ${itemBorder}`}>
      <div className="px-4 py-3 flex items-start gap-3">
        <div className={`text-[10px] font-mono font-bold uppercase tracking-widest px-2 py-1 border ${
          item.consumer_app === 'scrubber' ? 'border-sky-400 text-sky-700 bg-sky-50' : 'border-red-700 text-red-700 bg-red-50'
        }`}>{consumerKind}</div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-slate-900">{item.catalog_label}</div>
          <div className="text-[10px] font-mono text-slate-500 mt-0.5">{item.catalog_id}</div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className={`text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 border ${
            item.status === 'accepted' ? 'border-emerald-400 text-emerald-700 bg-emerald-50' :
            item.status === 'received' ? 'border-amber-500 text-amber-700 bg-amber-50' :
                                          'border-slate-300 text-slate-600 bg-slate-50'
          }`}>{item.status}</span>
          {submissions.length > 0 && (
            <button onClick={() => setOpen(!open)}
              className="text-[10px] font-mono uppercase tracking-widest text-slate-500 hover:text-slate-900 transition-colors">
              {open ? 'Hide' : `${submissions.length} ${submissions.length === 1 ? 'upload' : 'uploads'} →`}
            </button>
          )}
          {canReceive && item.status === 'pending' && !marking && (
            <button onClick={() => setMarking(true)}
              className="text-[10px] font-mono uppercase tracking-widest border border-slate-300 px-2 py-0.5 text-slate-700 hover:border-emerald-600 hover:text-emerald-700 transition-colors">
              Mark received
            </button>
          )}
        </div>
      </div>

      {item.status !== 'pending' && (item.received_via || item.received_at) && (
        <div className="border-t border-slate-100 px-4 py-2 text-xs text-slate-600">
          <span className="font-semibold text-slate-800">{receivedViaLabel(item.received_via)}</span>
          {item.received_by && item.received_via !== 'coa_match' ? <> · {item.received_by}</> : null}
          {item.received_at ? <> · {new Date(item.received_at).toLocaleString()}</> : null}
          {item.received_note ? <div className="mt-0.5 text-slate-700">{item.received_note}</div> : null}
        </div>
      )}

      {marking && (
        <div className="border-t border-slate-100 bg-slate-50/60 px-4 py-3">
          <div className="text-xs font-semibold text-slate-800">Mark this universe received</div>
          <div className="text-[11.5px] text-slate-600 mt-0.5">
            Use this when the universe arrived outside the portal and outside the Scrubber. Say how and when (SFTP, hand delivered, run without the link). The request completes when every universe is in.
          </div>
          <input type="text" value={markNote} onChange={(e) => setMarkNote(e.target.value.slice(0, 500))} maxLength={500}
            placeholder="How it arrived, and when"
            className="mt-2 w-full border border-slate-300 px-2 py-1.5 text-xs text-slate-900 focus:outline-none focus:border-slate-500" />
          {markError && <div className="text-[11.5px] text-red-700 mt-1">{markError}</div>}
          <div className="flex items-center gap-2 mt-2">
            <button disabled={markBusy} onClick={markReceived}
              className="bg-emerald-700 hover:bg-emerald-800 disabled:bg-slate-300 text-white px-3 py-1 text-xs font-semibold transition-colors">
              {markBusy ? 'Saving…' : 'Received'}
            </button>
            <button disabled={markBusy} onClick={() => { setMarking(false); setMarkError(null); }}
              className="text-xs text-slate-600 hover:text-slate-900">Cancel</button>
          </div>
        </div>
      )}

      {open && submissions.length > 0 && (
        <div className="border-t border-slate-100 bg-slate-50/50 p-4 space-y-3">
          {submissions.map((s, i) => (
            <SubmissionCard key={s.id} submission={s} isLatest={i === 0} onDownload={onDownload}/>
          ))}
        </div>
      )}
      {open && submissions.length === 0 && (
        <div className="border-t border-slate-100 px-4 py-3 text-xs text-slate-500">
          {recipientType === 'internal_department' ? 'No portal uploads for this universe.' : 'No uploads yet from the FDR.'}
        </div>
      )}
    </div>
  );
}

function receivedViaLabel(via) {
  switch (via) {
    case 'scrubber_match': return 'Received: seen in the Scrubber';
    case 'coa_match': return 'Received: result scored in COA';
    case 'compliance': return 'Received: marked by compliance';
    case 'internal_fulfillment': return 'Received: internal fulfillment';
    case 'portal': return 'Received: portal upload';
    default: return 'Received';
  }
}

function SubmissionCard({ submission, isLatest, onDownload }) {
  const summary = submission.validation_summary || {};
  const findings = summary.findings || [];
  const counts = summary.counts || null;
  const warnings = summary.warnings || [];
  const verdict = summary.verdict;
  const ok = !!summary.ok;
  const groups = useMemo(() => groupFindings(findings), [findings]);
  const [expanded, setExpanded] = useState(submission.finalized || isLatest);

  return (
    <div className={`bg-white border ${submission.finalized ? 'border-emerald-300' : 'border-slate-200'}`}>
      <div className="px-4 py-3 flex items-center justify-between gap-3 border-b border-slate-100">
        <div className="flex items-center gap-3 min-w-0">
          <div className={`w-2 h-2 rounded-full flex-shrink-0 ${ok ? 'bg-emerald-500' : 'bg-red-700'}`}/>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-slate-900 flex items-center gap-2">
              <span>Attempt {submission.attempt_number}</span>
              {submission.finalized && <span className="text-[10px] font-mono uppercase tracking-widest text-emerald-700 bg-emerald-50 border border-emerald-300 px-1.5 py-0.5">Final</span>}
              {isLatest && !submission.finalized && <span className="text-[10px] font-mono uppercase tracking-widest text-amber-700 bg-amber-50 border border-amber-300 px-1.5 py-0.5">Latest</span>}
            </div>
            <div className="text-[10px] font-mono text-slate-500 mt-0.5 truncate">
              {submission.file_name} · {Math.round(submission.file_size / 1024)} KB · sha256 {submission.file_hash_sha256?.slice(0, 12)}…
            </div>
            <div className="text-[10px] font-mono text-slate-500">
              Uploaded <span title={new Date(submission.submitted_at).toLocaleString()}>{friendlyPastTime(submission.submitted_at)}</span>
              {submission.submitted_by_email && <> · by {submission.submitted_by_email}</>}
              {submission.finalized_at && <> · finalized <span title={new Date(submission.finalized_at).toLocaleString()}>{friendlyPastTime(submission.finalized_at)}</span></>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button onClick={() => onDownload(submission.id, submission.file_name)}
            className="border border-slate-300 hover:border-red-700 hover:text-red-700 text-slate-700 px-3 py-1 text-xs font-semibold transition-colors">
            ↓ Download file
          </button>
          <button onClick={() => setExpanded(!expanded)}
            className="text-[10px] font-mono uppercase tracking-widest text-slate-500 hover:text-slate-900 transition-colors">
            {expanded ? 'Hide report' : 'View report →'}
          </button>
        </div>
      </div>

      <div className={`overflow-hidden transition-all duration-300 ease-out ${expanded ? 'max-h-[4000px] opacity-100' : 'max-h-0 opacity-0'}`}>
        <div className={`px-4 py-3 ${ok ? 'bg-emerald-50/40' : 'bg-slate-50/70'}`}>
          {verdict?.label && (
            <div className={`text-sm font-semibold mb-2 ${ok ? 'text-emerald-900' : 'text-slate-900'}`}>{verdict.label}</div>
          )}
          <div className="text-[10px] font-mono text-slate-600 mb-3">
            {summary.summary?.critical || 0} critical · {summary.summary?.high || 0} warnings · {summary.summary?.totalRows ?? '—'} rows · {summary.summary?.totalChecksRun || 0} checks
          </div>
          {counts && Object.keys(counts).length > 0 && (
            <div className="bg-white border border-slate-200 mb-3">
              <div className="px-3 py-2 border-b border-slate-100 text-[10px] font-mono uppercase tracking-widest text-slate-500">
                Counts derived from the FDR's file
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-6 divide-x divide-slate-100">
                {Object.entries(counts).filter(([, v]) => typeof v === 'number').map(([eid, val]) => (
                  <div key={eid} className="px-2 py-2 text-center">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Elt {eid}</div>
                    <div className="font-mono text-base text-slate-900 mt-0.5 tabular-nums">{val.toLocaleString()}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {groups.length === 0 ? (
            <div className="text-sm text-emerald-800">No findings — structurally clean.</div>
          ) : (
            <div className="space-y-2">
              {groups.map(g => <FindingGroup key={g.key} group={g}/>)}
            </div>
          )}
          {warnings.length > 0 && (
            <div className="mt-3 border-l-2 border-slate-300 bg-white px-3 py-2 text-xs text-slate-700">
              <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-1">Notes</div>
              <ul className="list-disc pl-4 space-y-0.5">{warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function FindingGroup({ group }) {
  const [open, setOpen] = useState(group.severity === 'critical');
  const sevColors =
    group.severity === 'critical' ? { border: 'border-red-700', text: 'text-red-700', bg: 'bg-red-50' } :
    group.severity === 'high'     ? { border: 'border-amber-600', text: 'text-amber-700', bg: 'bg-amber-50' } :
                                    { border: 'border-slate-300', text: 'text-slate-600', bg: 'bg-slate-50' };
  return (
    <div className={`bg-white border border-slate-200 border-l-4 ${sevColors.border}`}>
      <button onClick={() => setOpen(!open)} className="w-full px-3 py-2 text-left flex items-center gap-3 hover:bg-slate-50 transition-colors">
        <span className={`text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 border ${sevColors.border} ${sevColors.text} ${sevColors.bg} flex-shrink-0`}>{group.severity}</span>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-slate-900 leading-snug">{group.title}</div>
          <div className="text-[10px] font-mono text-slate-500 mt-0.5">{group.rule}</div>
        </div>
        <div className="text-xs text-slate-600">{group.items.length}</div>
        <div className={`text-slate-400 transition-transform duration-200 ${open ? 'rotate-90' : ''}`}>›</div>
      </button>
      {open && (
        <ul className="divide-y divide-slate-100 border-t border-slate-100 max-h-80 overflow-y-auto">
          {group.items.slice(0, 50).map((f, i) => (
            <li key={i} className="px-3 py-2 text-sm">
              <div className="flex items-start gap-3">
                <span className="text-[10px] font-mono text-slate-400 mt-0.5 w-14 flex-shrink-0">{f.row ? `row ${f.row}` : f.line ? `line ${f.line}` : '—'}</span>
                <div className="min-w-0 flex-1">
                  <div className="text-slate-900 leading-snug">{f.message}</div>
                  {f.citation && <div className="text-[10px] font-mono text-slate-500 mt-0.5 italic">{f.citation}</div>}
                </div>
              </div>
            </li>
          ))}
          {group.items.length > 50 && <li className="px-3 py-2 text-xs font-mono text-slate-500 bg-slate-50">…and {group.items.length - 50} more.</li>}
        </ul>
      )}
    </div>
  );
}

function groupFindings(findings) {
  const map = new Map();
  for (const f of findings) {
    const key = `${f.severity}:${f.rule || 'finding'}`;
    if (!map.has(key)) map.set(key, { key, severity: f.severity || 'high', rule: f.rule || 'finding', title: titleForRule(f.rule), items: [] });
    map.get(key).items.push(f);
  }
  return [...map.values()].sort((a, b) => (a.severity === 'critical' ? 0 : a.severity === 'high' ? 1 : 2) - (b.severity === 'critical' ? 0 : b.severity === 'high' ? 1 : 2));
}
function titleForRule(rule) {
  return ({
    header_mismatch:         'Header doesn\'t match the CMS layout',
    missing_required_field:  'Required field is empty',
    bad_coded_value:         'Value isn\'t in the CMS-approved set',
    bad_date_format:         'Date isn\'t in the format CMS expects',
    outside_reporting_period:'Row falls outside the reporting period',
    not_timely_notification: 'Notification wasn\'t delivered within the CMS timeframe',
    parse_error:             'File could not be parsed',
    no_validator:            'No validator configured for this universe',
  })[rule] || (rule || 'finding').replace(/_/g, ' ');
}
